import { PaletteMode, PaletteOptions } from '@mui/material';

import { designSystemColors } from '~/design-system/tokens/tokens';

import { colors } from './colors';

export type JustDoneThemeVariant = 'legacy' | 'homePageRedesign';

export type GetPaletteOptions = {
  variant?: JustDoneThemeVariant;
};

type JustDoneThemeColors = typeof colors;

type JustDonePaletteOptions = PaletteOptions & {
  common: Record<string, string>;
  secondary: Record<string, string>;
  primary: Record<string, string>;
  neutral: Record<string, string>;
  success: Record<string, string>;
  warning: Record<string, string>;
  error: Record<string, string>;
  gradient: Record<string, string>;
  surface: Record<string, string>;
  custom: Record<string, string>;
};

type DefaultBaseColors = {
  primary: Record<string, string>;
  secondary: Record<string, string>;
  tertiary: Record<string, never>;
  neutral: Record<string, string>;
  gradient: Record<string, string>;
  success: Record<string, string>;
  warning: Record<string, string>;
  error: Record<string, string>;
};

const homePageRedesignColors: JustDoneThemeColors = {
  black: designSystemColors.black,
  white: designSystemColors.white,

  primary_20: designSystemColors.neutral[700],
  primary_30: designSystemColors.neutral[700],
  primary_35: designSystemColors.neutral[800],
  primary_40: designSystemColors.neutral[900],
  primary_45: designSystemColors.neutral[800],
  primary_50: designSystemColors.blue[700],
  primary_60: designSystemColors.neutral[200],
  primary_70: designSystemColors.blue[100],
  primary_80: designSystemColors.neutral[300],
  primary_90: designSystemColors.neutral[200],

  secondary_40: designSystemColors.blue[100],
  secondary_80: designSystemColors.neutral[100],

  gradient_20: 'linear-gradient(90deg, #FF9CBF 0%, #FCD5E1 100%)',
  gradient_40: designSystemColors.neutral[900],
  gradient_60: 'linear-gradient(90deg, #C7D38B 0%, #F1EBB4 100%)',
  gradient_80: designSystemColors.white,

  error_20: colors.error_20,
  error_30: colors.error_30,
  error_40: colors.error_40,
  error_45: colors.error_45,
  error_50: colors.error_50,
  error_60: colors.error_60,
  error_80: colors.error_80,

  red_80: colors.red_80,

  neutral_10: designSystemColors.neutral[950],
  neutral_20: designSystemColors.neutral[500],
  neutral_30: designSystemColors.neutral[400],
  neutral_40: designSystemColors.neutral[300],
  neutral_60: designSystemColors.neutral[100],
  neutral_80: designSystemColors.neutral[100],
  neutral_90: designSystemColors.white,

  warning_40: designSystemColors.yellow[200],
  warning_50: designSystemColors.yellow[400],
  warning_60: designSystemColors.yellow[500],
  warning_80: designSystemColors.yellow[100],
  warning_90: designSystemColors.yellow[50],

  green_30: designSystemColors.green[800],
  green_50: designSystemColors.green[500],
  green_70: designSystemColors.green[300],
  green_80: designSystemColors.green[200],
  green_90: designSystemColors.green[100],

  accent_40: designSystemColors.green[100],

  highlight_40: designSystemColors.blue[300],
  highlight_50: designSystemColors.yellow[400],

  yellow_20: designSystemColors.yellow[500],
  yellow_30: designSystemColors.yellow[400],
  yellow_40: designSystemColors.yellow[400],
  // Distinct from yellow_30/yellow_40 so plagiarism-score breakdown items
  // (Identical/Minor changes/Omitted words) render as 3 visibly different
  // shades instead of 2 identical ones.
  yellow_50: designSystemColors.yellow[200],
  yellow_60: designSystemColors.yellow[100],
};

const createDefaultBaseColors = (
  themeColors: JustDoneThemeColors,
): DefaultBaseColors => ({
  primary: {
    700: themeColors.primary_20,
    500: themeColors.primary_40,
    100: themeColors.primary_80,
  },
  secondary: {
    700: themeColors.neutral_20,
    500: themeColors.neutral_30,
    100: themeColors.primary_80,
  },
  tertiary: {},
  neutral: {
    700: themeColors.neutral_20,
    500: themeColors.neutral_30,

    100: themeColors.neutral_60,
  },
  gradient: {
    500: themeColors.gradient_40,
  },
  success: {
    500: themeColors.green_50,
  },
  warning: {
    500: themeColors.warning_50,
  },
  error: {
    700: themeColors.error_30,
    500: themeColors.error_40,
    100: themeColors.error_80,
  },
});

const createCustomColors = (
  themeColors: JustDoneThemeColors,
): Record<string, string> => ({
  primary_20: themeColors.primary_20,
  primary_30: themeColors.primary_30,
  primary_35: themeColors.primary_35,
  primary_40: themeColors.primary_40,
  primary_45: themeColors.primary_45,
  primary_50: themeColors.primary_50,
  primary_70: themeColors.primary_70,
  primary_60: themeColors.primary_60,
  primary_80: themeColors.primary_80,
  primary_90: themeColors.primary_90,

  secondary_40: themeColors.secondary_40,
  secondary_80: themeColors.secondary_80,

  gradient_20: themeColors.gradient_20,
  gradient_40: themeColors.gradient_40,
  gradient_60: themeColors.gradient_60,
  gradient_80: themeColors.gradient_80,

  error_20: themeColors.error_20,
  error_30: themeColors.error_30,
  error_40: themeColors.error_40,
  error_45: themeColors.error_45,
  error_50: themeColors.error_50,
  error_60: themeColors.error_60,
  error_80: themeColors.error_80,

  neutral_10: themeColors.neutral_10,
  neutral_20: themeColors.neutral_20,
  neutral_30: themeColors.neutral_30,
  neutral_40: themeColors.neutral_40,
  neutral_60: themeColors.neutral_60,
  neutral_80: themeColors.neutral_80,
  neutral_90: themeColors.neutral_90,

  warning_40: themeColors.warning_40,
  warning_50: themeColors.warning_50,
  warning_60: themeColors.warning_60,
  warning_80: themeColors.warning_80,
  warning_90: themeColors.warning_90,

  red_80: themeColors.red_80,

  green_30: themeColors.green_30,
  green_50: themeColors.green_50,
  green_70: themeColors.green_70,
  green_80: themeColors.green_80,
  green_90: themeColors.green_90,

  accent_40: themeColors.accent_40,

  highlight_40: themeColors.highlight_40,
  highlight_50: themeColors.highlight_50,

  yellow_20: themeColors.yellow_20,
  yellow_30: themeColors.yellow_30,
  yellow_40: themeColors.yellow_40,
  yellow_50: themeColors.yellow_50,
  yellow_60: themeColors.yellow_60,
});

export const getPalette = (
  mode: PaletteMode,
  options: GetPaletteOptions = {},
): JustDonePaletteOptions => {
  const themeColors =
    options.variant === 'homePageRedesign' ? homePageRedesignColors : colors;
  const defaultBaseColors = createDefaultBaseColors(themeColors);
  const customColors = createCustomColors(themeColors);

  return {
    ...(mode === 'light'
      ? {
          common: {
            black: themeColors.black,
            white: themeColors.white,
          },
          mode: 'light',
          background: {
            paper: themeColors.white,
          },
          primary: {
            main: themeColors.primary_40,
            ...defaultBaseColors.primary,
          },
          secondary: {
            main: themeColors.neutral_40,
            ...defaultBaseColors.secondary,
          },
          tertiary: {
            ...defaultBaseColors.tertiary,
          },
          gradient: {
            main: themeColors.gradient_40,
            ...defaultBaseColors.gradient,
          },
          neutral: {
            main: themeColors.neutral_40,
            ...defaultBaseColors.neutral,
          },
          success: {
            main: themeColors.green_50,
            ...defaultBaseColors.success,
          },
          warning: {
            main: themeColors.warning_50,
            ...defaultBaseColors.warning,
          },
          error: {
            main: themeColors.error_40,
            ...defaultBaseColors.error,
          },
          surface: {
            main: themeColors.white,
          },
          custom: {
            ...customColors,
          },
        }
      : {
          common: {
            black: themeColors.white,
            white: themeColors.black,
          },
          mode: 'dark',
          background: {
            paper: themeColors.black,
          },
          primary: {
            main: themeColors.primary_40,
            ...defaultBaseColors.primary,
          },
          secondary: {
            main: themeColors.neutral_40,
            ...defaultBaseColors.secondary,
          },
          tertiary: {
            ...defaultBaseColors.tertiary,
          },
          gradient: {
            main: themeColors.gradient_40,
            ...defaultBaseColors.gradient,
          },
          neutral: {
            main: themeColors.neutral_40,
            ...defaultBaseColors.neutral,
          },
          success: {
            main: themeColors.green_50,
            ...defaultBaseColors.success,
          },
          warning: {
            main: themeColors.warning_50,
            ...defaultBaseColors.warning,
          },
          error: {
            main: themeColors.error_30,
            ...defaultBaseColors.error,
          },
          surface: {
            main: themeColors.black,
          },
          custom: {
            ...customColors,
          },
        }),
  };
};
